import { formatScore } from "./format"
import type { Corte, Faixa, ProvaSocial, SubjectResult } from "../types"

/**
 * A narrativa do produto, espelho de app/services/narrativa.rb: o simulado é o
 * mecanismo, a transformação é chegar à prova sabendo que classifica. Uma
 * frase só para cada coisa, usada em toda tela — nunca reescrita no lugar.
 *
 * Vocabulário proibido na manchete: "acesso", "ilimitado", "dias", "todos os
 * simulados", "pacote", "plano". Podem existir como detalhe, ao lado do preço.
 */
export const PROMESSA = "Chegue à prova sabendo que classifica."
export const MECANISMO = "Descubra por onde começar, treine no formato exato da banca e saiba quando está pronto."
export const CRITERIO_DE_PRONTO = "Pronto é passar do corte do último concurso em dois simulados oficiais seguidos."

/**
 * Diagnóstico → tratamento (decisão de 08/09/2026). O grátis se apresentava
 * como produto completo ("1 treino grátis por matéria"); "diagnóstico" troca a
 * categoria mental e faz o plano virar o próximo passo, não um upgrade
 * opcional. "Nivelamento" segue sendo o nome do quiz; "Diagnóstico gratuito" é
 * o nome do pacote grátis. "Treino" agora nomeia só o que o plano destrava.
 */
export const DIAGNOSTICO = "Diagnóstico gratuito"
/** O par, numa linha: encabeça a seção de preço e a página do plano. */
export const TRATAMENTO_LEAD = "Diagnóstico mostra onde. Tratamento leva até lá."
/** "Diagnóstico" pede meia frase de contexto onde "treino grátis" não pedia. */
export const DIAGNOSTICO_SUBTITULO = "Descubra suas matérias de partida em 10 questões."
export const DIAGNOSTICO_ESCOPO = "o nivelamento e um diagnóstico por matéria"

/** O outro lado da metáfora: o plano é o tratamento. Diagnóstico não aprova ninguém. */
export const TRATAMENTO_CONTRASTE = "O diagnóstico mostrou o problema. O tratamento fecha a distância."
/** Acima do corte não há "problema" a mostrar; a frase padrão soaria falsa ali. */
export const TRATAMENTO_CONTRASTE_ACIMA = "O diagnóstico mostrou que dá. O tratamento transforma isso em consistência."
/**
 * Uma palavra por ação, em todo botão. Grátis é sempre "diagnóstico"
 * (nivelamento fica pro corpo, explicando o mecanismo); pago é sempre
 * "tratamento". Espelho de Narrativa.
 */
export const CTA_DIAGNOSTICO = "Fazer meu diagnóstico grátis"
export const CTA_TRATAMENTO = "Começar meu tratamento"
export const CTA_TRATAMENTO_CONTINUAR = "Continuar meu tratamento"
export const GARANTIA = "Sem fidelidade · reembolso integral em 7 dias se não for pra você."
/**
 * Desarma a vergonha de errar no nivelamento com número nosso. MEDIDO em
 * 08/09/2026 sobre o primeiro simulado finalizado de 122 usuários não-admin em
 * produção: erro médio 73,8%, mediano 80,0%, 69,7% erraram 70% ou mais.
 * Espelho de Narrativa::ERRO_NO_PRIMEIRO — remedir antes de mexer no número.
 */
export const ERRO_NO_PRIMEIRO = "a maioria erra 70% no primeiro"
export const OBJECAO_NUMERO = "quem faz o teste antes de estudar acerta mais depois: 75% contra 56% de quem só estudou o texto"
export const OBJECAO_FONTE = "https://doi.org/10.1037/a0016496"
export const OBJECAO_FONTE_LABEL = "Richland, Kornell e Kao, 2009"

export function promessaPara(sigla: string): string {
  return `Chegue à ${sigla} sabendo que classifica.`
}

/** Os quatro pilares da preparação, na ordem do ciclo. O quarto é o critério. */
export function pilares(criterio: string = CRITERIO_DE_PRONTO): [string, string][] {
  return [
    // NÃO chamar de "nivelamento": esse é o nome do quiz grátis, e vender o
    // grátis como feature do plano desmonta a metáfora.
    ["Diagnóstico a cada simulado", "matéria por matéria: descubra exatamente o que está derrubando sua nota."],
    ["Estudo pelos erros", "cada questão oficial com a resposta certa e o comentário — a aula está no gabarito."],
    ["Treino no formato da banca", "o mesmo número de questões, o mesmo tempo e a mesma distribuição da prova."],
    ["Saber quando está pronto", `${criterio} Aí ${PROMESSA.charAt(0).toLowerCase()}${PROMESSA.slice(1)}`],
  ]
}

/** Passaria do corte? Só quando existe corte publicado para a escola. */
export function passariaDoCorte(score: number, corte: Corte | null): boolean | null {
  return corte ? score >= corte.nota : null
}

/**
 * Matérias em que a pessoa perdeu ponto, da mais fraca para a menos fraca —
 * a lista INTEIRA. Citar só duas enquanto a tabela mostra cinco faz parecer
 * que o sistema não viu as outras.
 */
export function materiasDePartida(results: SubjectResult[]): SubjectResult[] {
  return [...results]
    .filter((r) => r.total > 0 && r.correct < r.total)
    .sort((a, b) => a.correct / a.total - b.correct / b.total)
}

export function materiasFechadas(results: SubjectResult[]): SubjectResult[] {
  return results.filter((r) => r.total > 0 && r.correct === r.total)
}

/**
 * Todas as matérias, nome por nome. É o que a revelação do diagnóstico pede:
 * "mostrou onde você perde ponto: Matemática, Português e mais três" seria
 * justamente esconder o que a pessoa veio ver.
 */
export function listarMaterias(fracas: SubjectResult[]): string {
  const nomes = fracas.map((r) => r.subject)
  if (nomes.length <= 1) return nomes.join("")
  return `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`
}

/** Até três, nome por nome; acima disso o número diz mais. */
export function nomearLacunas(fracas: SubjectResult[]): string {
  const nomes = fracas.map((r) => r.subject)
  if (nomes.length > 3) return `as ${nomes.length} matérias em que você perdeu ponto`
  if (nomes.length <= 1) return nomes.join("")
  return `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`
}

/**
 * "2,3 pontos" / "1,0 ponto": quanto falta pro corte. Só quando existe corte
 * publicado e a pessoa ficou abaixo dele — é o número que vira manchete no
 * pitch de quem ainda não classificaria.
 */
export function distanciaDoCorte(score: number, corte: Corte | null): string | null {
  if (!corte || score >= corte.nota) return null
  const d = Math.round((corte.nota - score) * 10) / 10
  return `${formatScore(d)} ${d === 1 ? "ponto" : "pontos"}`
}

/** "2.157 questões oficiais comentadas, de 2013 a 2026" — o que o banco sustenta, nada além. */
export function provaSocialLabel(ps: ProvaSocial | undefined): string | null {
  if (!ps || ps.questoes <= 0) return null
  const base = `${ps.questoes.toLocaleString("pt-BR")} questões oficiais comentadas`
  if (ps.ano_inicial && ps.ano_final && ps.ano_inicial !== ps.ano_final) return `${base}, de ${ps.ano_inicial} a ${ps.ano_final}`
  return base
}

/** "entre 7,5 e 8,0" — faixa de referência, nunca apresentada como o corte. */
export function faixaLabel(faixa: Faixa): string {
  return `entre ${formatScore(faixa.de)} e ${formatScore(faixa.ate)}`
}

export function corteLabel(corte: Corte): string {
  return `${formatScore(corte.nota)} em ${corte.ano}`
}
